import { BOUNDARY_CHANGE_NOTICE } from "./precinctComparison";
import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MapPane,
  buildApiFetchOptions,
  buildAreaDataUrl,
  buildAreaElectionsUrl,
  formatInt,
  formatPct,
  getOkrsekTail,
} from "./Map";
import { ELECTION_MAP_STANDARD_V1_KEY, getElectionMapFeatureStyle } from "./mapRenderingStandard";
import {
  buildAreaBaselinePoints,
  TRENDS_FAMILY_CONFIG,
  TRENDS_FAMILY_ORDER,
  TRENDS_METRICS,
  TRENDS_SORT_OPTIONS,
  buildAppliedQueryFromDraft,
  buildDefaultDraftQuery,
  buildTrendRows,
  buildTrendsElectionMatrix,
  createEmptyTrendsElectionMatrix,
  filterAndSortTrendRows,
  formatTrendDeltaValue,
  getDefaultTrendsFamily,
  getEntityOptionsForBundle,
  getMetricValueFromStats,
  getStatePresentation,
  isDraftQueryValid,
  normalizeTrendsQuerySnapshot,
} from "./trends";
import { APP_PAGE_BACKGROUND, BRAND_COLORS } from "./branding";
import { sortElectionsNewestFirst } from "./electionOrdering";
import {
  readElectionSelectionContext,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import {
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";

const PARTY_ANALYSIS_MODE = "party";
const METRIC_ANALYSIS_MODE = "metric";

function parseJsonSafe(response) {
  return response.json().catch(() => null);
}

async function requestJson(url, signal, apiBase = "/api") {
  const response = await fetch(url, buildApiFetchOptions(apiBase, { signal }));
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    const message =
      payload && typeof payload.error === "string" && payload.error
        ? payload.error
        : `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function formatCompactValue(value, config) {
  if (!Number.isFinite(value)) return "N/A";
  if (config.analysisMode === METRIC_ANALYSIS_MODE && config.metricKey === "turnout_pct") {
    return formatPct(value);
  }
  if (config.analysisMode === PARTY_ANALYSIS_MODE && config.valueMode === "percentage") {
    return formatPct(value);
  }
  return formatInt(value);
}

function formatErrorMessage(error, fallback) {
  if (error && typeof error.message === "string" && error.message) return error.message;
  return fallback;
}

function sortEntitySelections(selections) {
  return (Array.isArray(selections) ? selections : [])
    .slice()
    .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), "cs"));
}

function getTrendStateMeta(row) {
  const state = row && row.state ? row.state : {};
  const presentation = getStatePresentation(state.kind);
  return {
    kind: state.kind || "no_data",
    key: state.key || "N/A",
    label: state.label || "Nedostatečná data",
    fill: presentation.fill,
    stroke: presentation.stroke,
  };
}

function getLowerSectionDistrictLabel(row) {
  return `Okr. ${row && row.precinctCode ? row.precinctCode : ""}`.trim();
}

function getStateFilterOptions(selectedYearsCount) {
  return selectedYearsCount === 2
    ? [
        { key: "all", label: "Vše" },
        { key: "positive_only", label: "Jen růst" },
        { key: "neutral_only", label: "Jen beze změny" },
        { key: "negative_only", label: "Jen pokles" },
        { key: "mismatch_only", label: "Jen N/A" },
      ]
    : [
        { key: "all", label: "Vše" },
        { key: "mixed_only", label: "Jen mixed" },
        { key: "pure_only", label: "Jen pure" },
        { key: "flat_only", label: "Obsahuje 0" },
        { key: "mismatch_only", label: "Jen N/A" },
      ];
}

function buildLowerSectionRows(rows, options) {
  return filterAndSortTrendRows(rows, options);
}

function restoreTrendsDraftQuery(matrix, storedDraft) {
  const fallback = buildDefaultDraftQuery(matrix);
  const families = TRENDS_FAMILY_ORDER;
  const storedFamily =
    storedDraft &&
    families.includes(storedDraft.family) &&
    Array.isArray(matrix && matrix[storedDraft.family]) &&
    matrix[storedDraft.family].length > 0
      ? storedDraft.family
      : "";
  const family = storedFamily || fallback.family;
  const familyYears = Array.isArray(matrix && matrix[family]) ? matrix[family] : [];
  const allowedYears = new Set(familyYears.map((item) => item.year));
  const defaultYearsForFamily = familyYears.map((item) => item.year);
  const storedYears = (Array.isArray(storedDraft && storedDraft.selectedYears) ? storedDraft.selectedYears : [])
    .map((year) => Number(year))
    .filter((year) => allowedYears.has(year));
  const selectedYears =
    storedYears.length >= 2 && storedYears.length <= 3
      ? storedYears
      : defaultYearsForFamily;
  const analysisMode = storedDraft && storedDraft.analysisMode === METRIC_ANALYSIS_MODE ? METRIC_ANALYSIS_MODE : PARTY_ANALYSIS_MODE;

  return {
    ...fallback,
    family,
    selectedYears,
    analysisMode,
    valueMode: storedDraft && storedDraft.valueMode === "absolute" ? "absolute" : "percentage",
    metricKey:
      storedDraft && typeof storedDraft.metricKey === "string" && storedDraft.metricKey.trim()
        ? storedDraft.metricKey.trim()
        : fallback.metricKey,
    entitySelections:
      storedDraft && storedDraft.entitySelections && typeof storedDraft.entitySelections === "object"
        ? storedDraft.entitySelections
        : {},
  };
}

function normalizeStoredObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function getSupportMetricLine(row, config) {
  const stats = row && row.supportStats ? row.supportStats : null;
  if (!stats) return "";
  if (config.analysisMode === METRIC_ANALYSIS_MODE && config.metricKey === "eligible_voters") {
    return "";
  }
  const eligible = getMetricValueFromStats(stats, "eligible_voters");
  if (!eligible.available || !Number.isFinite(eligible.value)) return "";
  return `Oprávnění voliči: ${formatInt(eligible.value)}`;
}

function buildTooltipFields(row, config, appliedQuery) {
  const yearsLine = appliedQuery.years.map((entry) => entry.year).join(" · ");
  const valuesLine = row.points.map((point) => formatCompactValue(point.value, config)).join(" · ");
  const deltaLines = row.state.deltas.map((delta, index) => ({
    label: `Δ${index + 1}`,
    value: formatTrendDeltaValue(delta, config),
  }));
  return {
    title: row.districtLabel,
    support: getSupportMetricLine(row, config),
    yearsLine,
    valuesLine,
    deltaLines,
    finalState: row.state.key,
    finalLabel: row.state.label,
  };
}

function buildBaselinePreview(areaBaselinePoints, config) {
  const points = Array.isArray(areaBaselinePoints) ? areaBaselinePoints : [];
  return points
    .map((point) => formatCompactValue(point && point.available ? point.value : null, config))
    .filter((value) => value !== "N/A");
}

function getDeltaTone(delta) {
  if (!Number.isFinite(delta) || Math.abs(delta) < 1e-9) return "neutral";
  return delta > 0 ? "positive" : "negative";
}

function Sparkline({ row, config, showBaseline }) {
  const width = 138;
  const height = 44;
  const paddingX = 9;
  const paddingY = 9;
  const points = Array.isArray(row && row.points) ? row.points : [];
  const baselinePoints =
    showBaseline && row && Array.isArray(row.areaBaselinePoints) ? row.areaBaselinePoints : [];
  const finiteValues = points
    .map((point) => point.value)
    .concat(baselinePoints.map((point) => point.value))
    .filter((value) => Number.isFinite(value));
  if (finiteValues.length === 0) {
    return <div className="trends-sparkline-empty">N/A</div>;
  }
  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = Math.max(max - min, 1);
  const stepX = points.length > 1 ? (width - paddingX * 2) / (points.length - 1) : 0;

  const projectPoint = (point, index) => {
    const x = paddingX + stepX * index;
    if (!Number.isFinite(point && point.value)) return { x, y: height / 2, missing: true, value: null };
    const ratio = (Number(point.value) - min) / range;
    const y = height - paddingY - ratio * (height - paddingY * 2);
    return { x, y, missing: false, value: point.value };
  };

  const projected = points.map(projectPoint);
  const projectedBaseline = baselinePoints.map(projectPoint);

  const buildSegments = (series) => {
    const segments = [];
    for (let index = 1; index < series.length; index += 1) {
      const prev = series[index - 1];
      const next = series[index];
      if (prev.missing || next.missing) continue;
      segments.push(`M ${prev.x} ${prev.y} L ${next.x} ${next.y}`);
    }
    return segments;
  };

  const districtSegments = buildSegments(projected);
  const baselineSegments = buildSegments(projectedBaseline);
  const bandSegments = [];
  if (showBaseline) {
    for (let index = 1; index < projected.length; index += 1) {
      const prevDistrict = projected[index - 1];
      const nextDistrict = projected[index];
      const prevBaseline = projectedBaseline[index - 1];
      const nextBaseline = projectedBaseline[index];
      if (
        !prevBaseline ||
        !nextBaseline ||
        prevDistrict.missing ||
        nextDistrict.missing ||
        prevBaseline.missing ||
        nextBaseline.missing
      ) {
        continue;
      }
      const districtOnSegment =
        (Number(prevDistrict.value) + Number(nextDistrict.value)) / 2;
      const baselineOnSegment =
        (Number(prevBaseline.value) + Number(nextBaseline.value)) / 2;
      const isDistrictAboveBaseline = districtOnSegment > baselineOnSegment;
      bandSegments.push({
        key: `${row.precinctId}-band-${index}`,
        tone: isDistrictAboveBaseline ? "positive" : "negative",
        fill: isDistrictAboveBaseline ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
        d: `M ${prevDistrict.x} ${prevDistrict.y} L ${nextDistrict.x} ${nextDistrict.y} L ${nextBaseline.x} ${nextBaseline.y} L ${prevBaseline.x} ${prevBaseline.y} Z`,
      });
    }
  }

  const districtLabelPositions = projected.map((point, index) => {
    if (point.missing) return { ...point, labelY: point.y, labelX: point.x, anchor: "middle" };
    const isNearTop = point.y < paddingY + 8;
    const labelY = isNearTop ? point.y + 10 : point.y - 7;
    let labelX = point.x;
    let anchor = "middle";
    if (index === 0) {
      labelX = point.x + 4;
      anchor = "start";
    } else if (index === projected.length - 1) {
      labelX = point.x - 4;
      anchor = "end";
    }
    return { ...point, labelY, labelX, anchor, isNearTop };
  });
  const pointLabelWidth = 22;
  const pointLabelHeight = 8.5;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="trends-sparkline">
      <rect x="0" y="0" width={width} height={height} rx="10" fill="#f8fafc" />
      {bandSegments.map((segment) => (
        <path
          key={segment.key}
          d={segment.d}
          fill={segment.fill}
          stroke="none"
          data-testid={segment.key}
          data-band-tone={segment.tone}
        />
      ))}
      {showBaseline
        ? baselineSegments.map((segment, index) => (
            <path
              key={`baseline-${index}`}
              d={segment}
              fill="none"
              stroke="#94a3b8"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeDasharray="3 2"
            />
          ))
        : null}
      {districtSegments.map((segment, index) => (
        <path key={index} d={segment} fill="none" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" />
      ))}
      {projected.map((point, index) => (
        <g key={index}>
          <circle
            cx={point.x}
            cy={point.y}
            r="3.4"
            fill={point.missing ? "#cbd5e1" : "#ffffff"}
            stroke={point.missing ? "#94a3b8" : "#2563eb"}
            strokeWidth="2"
          />
        </g>
      ))}
      {districtLabelPositions.map((point, index) =>
        point.missing ? null : (
          <g key={`label-${index}`}>
            <rect
              x={
                point.anchor === "start"
                  ? point.labelX - 2
                  : point.anchor === "end"
                    ? point.labelX - pointLabelWidth
                    : point.labelX - pointLabelWidth / 2
              }
              y={point.labelY - 6.2}
              rx="3.5"
              width={pointLabelWidth}
              height={pointLabelHeight}
              fill="rgba(255,255,255,0.94)"
              stroke="#dbe4ef"
            />
            <text
              x={point.labelX}
              y={point.labelY}
              textAnchor={point.anchor}
              className="trends-sparkline-point-label"
            >
              {formatCompactValue(point.value, config)}
            </text>
          </g>
        )
      )}
    </svg>
  );
}

function LegendSwatch({ kind }) {
  const { fill, stroke } = getStatePresentation(kind);
  return <span className="trends-legend-swatch" style={{ background: fill, borderColor: stroke }} aria-hidden="true" />;
}

function QueryYearButton({ selected, disabled, label, onClick }) {
  return (
    <button
      type="button"
      className={`trends-year-chip ${selected ? "is-selected" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function EntitySelector({
  yearEntry,
  bundle,
  selectedEntities,
  onAddEntity,
  onRemoveEntity,
  pendingValues,
  setPendingValues,
  isLoading,
}) {
  const electionId = yearEntry.election.id;
  const options = useMemo(() => getEntityOptionsForBundle(bundle), [bundle]);
  const selectedKeys = new Set((selectedEntities || []).map((item) => item.key));
  const availableOptions = options.filter((option) => !selectedKeys.has(option.key));
  const pendingValue = pendingValues[electionId] || "";
  const hasSelectableEntity = availableOptions.length > 0;
  const helperText = isLoading
    ? "Načítám entity pro tento rok."
    : hasSelectableEntity
      ? "Vyber jednu nebo více stran."
      : (selectedEntities || []).length > 0
        ? "Pro tento rok už je výběr hotový. Pokračuj dalším rokem a potom klikni na „Ukázat trendy“."
        : "Pro tento rok už není k dispozici žádná další entita.";

  return (
    <div className="trends-entity-panel">
      <div className="trends-entity-header">
        <strong>{yearEntry.year}</strong>
        <span>{yearEntry.election.display_name || yearEntry.election.name || yearEntry.election.id}</span>
      </div>
      <div className="trends-entity-controls">
        <select
          value={pendingValue}
          onChange={(event) => {
            const nextValue = event.target.value;
            const next = availableOptions.find((option) => option.key === nextValue);
            if (next) {
              onAddEntity(electionId, next);
            }
            setPendingValues((current) => ({
              ...current,
              [electionId]: "",
            }));
          }}
          className="trends-select"
          disabled={isLoading || availableOptions.length === 0}
        >
          <option value="">
            {isLoading ? "Načítám entity…" : availableOptions.length > 0 ? "Strana" : "Žádné další entity"}
          </option>
          {availableOptions.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="trends-entity-guidance">{helperText}</div>
      <div className="trends-chip-row">
        {(selectedEntities || []).length > 0 ? (
          sortEntitySelections(selectedEntities).map((item) => (
            <button
              key={item.key}
              type="button"
              className="trends-selection-chip"
              onClick={() => onRemoveEntity(electionId, item.key)}
              title="Odebrat"
            >
              <span>{item.label}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))
        ) : (
          <div className="trends-empty-hint">Pro tento rok zatím není vybraná žádná strana.</div>
        )}
      </div>
    </div>
  );
}

function LowerToolbar({
  viewMode,
  setViewMode,
  showBaselineArea,
  setShowBaselineArea,
  baselinePreview,
}) {
  return (
    <div className="trends-lower-toolbar">
      <div className="trends-segment-row" data-testid="trends-toolbar-view-mode">
        <button
          type="button"
          className={`trends-segment ${viewMode === "graph" ? "is-active" : ""}`}
          onClick={() => setViewMode("graph")}
        >
          Graf
        </button>
        <button
          type="button"
          className={`trends-segment ${viewMode === "list" ? "is-active" : ""}`}
          onClick={() => setViewMode("list")}
        >
          Seznam
        </button>
      </div>

      <label className="trends-toolbar-field" data-testid="trends-toolbar-baseline">
        <span>Celkový výsledek</span>
        <button
          type="button"
          className={`trends-segment trends-baseline-toggle ${showBaselineArea ? "is-active" : ""}`}
          aria-pressed={showBaselineArea}
          onClick={() => setShowBaselineArea((current) => !current)}
        >
          <span className="trends-baseline-toggle-label">Promítnout celkové výsledky</span>
          {baselinePreview.values.length > 0 ? (
            <>
              <span className="trends-baseline-toggle-separator">: </span>
              <span className="trends-baseline-toggle-values">{baselinePreview.values.join(" · ")}</span>
            </>
          ) : null}
        </button>
      </label>
    </div>
  );
}

function TrendGraphView({
  rows,
  config,
  onHoverRow,
  onLeaveRow,
  onToggleSelectRow,
  showBaseline,
  hoveredPrecinct,
  selectedPrecinctIds,
}) {
  if (rows.length === 0) {
    return <div className="trends-empty-panel">Aktuální filtr nevrátil žádné okrsky.</div>;
  }
  return (
    <div className="trends-graph-grid">
      {rows.map((row) => {
        const stateMeta = getTrendStateMeta(row);
        const isHovered = hoveredPrecinct === row.precinctId;
        const isSelected = selectedPrecinctIds.has(String(row.precinctId));
        return (
          <div
            key={row.precinctId}
            className={`trends-graph-card ${isHovered ? "is-hovered" : ""} ${isSelected ? "is-selected" : ""}`}
            onMouseEnter={() => onHoverRow(row.precinctId)}
            onMouseLeave={onLeaveRow}
            onClick={() => onToggleSelectRow(row.precinctId)}
            data-testid={`trend-graph-card-${row.precinctId}`}
          >
            <div className="trends-graph-header">
              <div>
                <div className="trends-graph-title">{getLowerSectionDistrictLabel(row)}</div>
                <div className="trends-graph-subtitle">{row.points.map((point) => formatCompactValue(point.value, config)).join(" · ")}</div>
              </div>
              <span className="trends-state-pill" style={{ borderColor: stateMeta.stroke }}>
                {row.state.key}
              </span>
            </div>
            <Sparkline row={row} config={config} showBaseline={showBaseline} />
            <div className="trends-graph-footer">
              <span>{row.state.label}</span>
              <strong>{formatTrendDeltaValue(row.netDelta, config)}</strong>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendListView({
  rows,
  config,
  appliedQuery,
  onHoverRow,
  onLeaveRow,
  onToggleSelectRow,
  showBaseline,
  hoveredPrecinct,
  selectedPrecinctIds,
}) {
  if (rows.length === 0) {
    return <div className="trends-empty-panel">Aktuální filtr nevrátil žádné okrsky.</div>;
  }
  const renderValueCell = (row, point) => (
    <td
      key={`${row.precinctId}-${point.electionId}`}
      className={point === row.points[row.points.length - 1] ? "trends-table-group-end" : ""}
    >
      <div>{formatCompactValue(point.value, config)}</div>
      {showBaseline ? (
        <div className="trends-table-secondary">
          {`Area: ${formatCompactValue(
            ((row.areaBaselinePoints || []).find((baselinePoint) => baselinePoint.electionId === point.electionId) || {}).value,
            config
          )}`}
        </div>
      ) : null}
    </td>
  );

  const renderDeltaCell = (row, delta, index) => (
    <td
      key={`${row.precinctId}-delta-${index}`}
      className={`trends-table-delta is-${getDeltaTone(delta)}`}
    >
      {formatTrendDeltaValue(delta, config)}
    </td>
  );

  return (
    <div className="trends-table-wrap">
      <table className="trends-table">
        <thead>
          <tr>
            <th className="trends-table-group-end">Okr.</th>
            {appliedQuery.years.map((entry, index) => (
              <th
                key={entry.election.id}
                className={index === appliedQuery.years.length - 1 ? "trends-table-group-end" : ""}
              >
                {entry.year}
              </th>
            ))}
            {appliedQuery.years.slice(1).map((entry, index) => (
              <th key={`${entry.election.id}-delta`}>{`Δ${index + 1}`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.precinctId}
              onMouseEnter={() => onHoverRow(row.precinctId)}
              onMouseLeave={onLeaveRow}
              onClick={() => onToggleSelectRow(row.precinctId)}
              className={`${hoveredPrecinct === row.precinctId ? "is-hovered" : ""} ${
                selectedPrecinctIds.has(String(row.precinctId)) ? "is-selected" : ""
              }`}
              data-testid={`trend-table-row-${row.precinctId}`}
            >
              <td className="trends-table-group-end">{getLowerSectionDistrictLabel(row)}</td>
              {row.points.map((point) => renderValueCell(row, point))}
              {row.state.deltas.map((delta, index) => renderDeltaCell(row, delta, index))}
              {row.state.deltas.length < appliedQuery.years.length - 1
                ? Array.from({ length: appliedQuery.years.length - 1 - row.state.deltas.length }, (_, index) => (
                    <td key={`${row.precinctId}-missing-delta-${index}`} className="trends-table-delta is-neutral">
                      N/A
                    </td>
                  ))
                : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptySurface({ title, text }) {
  return (
    <div className="trends-empty-surface">
      <div className="trends-empty-title">{title}</div>
      <div className="trends-empty-text">{text}</div>
    </div>
  );
}

function TrendsStickyMiniMap({
  visible,
  districtsGeoJson,
  mapKey,
  geoJsonKey,
  styleFeature,
  onEachFeature,
  onMapReady,
  onGeoJsonReady,
  onViewChange,
  onMouseLeave,
  initialView,
  fitBoundsRequestKey,
  onResetView,
}) {
  if (!visible || !districtsGeoJson) {
    return (
      <div className="trends-lower-rail-placeholder">
        Pravý panel je připravený pro budoucí sticky mini-map.
      </div>
    );
  }

  return (
    <div className="trends-sticky-mini-map" data-testid="trends-sticky-mini-map">
      <MapPane
        mapKey={mapKey}
        geoJsonKey={geoJsonKey}
        districtsGeoJson={districtsGeoJson}
        styleFeature={styleFeature}
        onEachFeature={onEachFeature}
        mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
        height="clamp(560px, calc(var(--app-shell-available-height, 100vh) - 32px), 760px)"
        fitGeoJsonBounds
        fitBoundsRequestKey={fitBoundsRequestKey}
        fitBoundsPadding={[18, 18]}
        fitBoundsMaxZoom={15}
        initialView={initialView}
        scrollWheelZoom={false}
        dragging
        doubleClickZoom={false}
        touchZoom={false}
        zoomControl
        showAttribution={false}
        showLabelsLayer
        geoJsonInteractive
        onMapReady={onMapReady}
        onViewChange={onViewChange}
        onGeoJsonReady={onGeoJsonReady}
        onMouseLeave={onMouseLeave}
        onResetView={onResetView}
      />
    </div>
  );
}

export default function TrendsPage({ areaId, apiBase = "/api" }) {
  const [electionsMatrix, setElectionsMatrix] = useState(() => createEmptyTrendsElectionMatrix());
  const [isLoadingElections, setIsLoadingElections] = useState(false);
  const [electionsError, setElectionsError] = useState("");
  const [draftQuery, setDraftQuery] = useState(() => buildDefaultDraftQuery(createEmptyTrendsElectionMatrix()));
  const [selectorBundles, setSelectorBundles] = useState({});
  const [selectorError, setSelectorError] = useState("");
  const [isLoadingSelectors, setIsLoadingSelectors] = useState(false);
  const [pendingEntityValues, setPendingEntityValues] = useState({});

  const [resolvedRun, setResolvedRun] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState("");

  const [viewMode, setViewMode] = useState("graph");
  const [sortKey, setSortKey] = useState("district_code");
  const [stateFilter, setStateFilter] = useState("all");
  const [showBaselineArea, setShowBaselineArea] = useState(true);
  const [hoveredPrecinct, setHoveredPrecinct] = useState(null);
  const [selectedPrecinctIds, setSelectedPrecinctIds] = useState(() => new Set());
  const [tooltip, setTooltip] = useState(null);

  const mapRef = useRef(null);
  const miniMapRef = useRef(null);
  const bundleCacheRef = useRef(new Map());
  const lastAreaIdRef = useRef(areaId);
  const hasRestoredSelectionRef = useRef(false);
  const initialViewport = useMemo(() => getInitialMapView("trends", areaId), [areaId]);
  const initialMiniViewport = useMemo(() => getInitialMapView("trends-mini", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("trends", areaId)));
  const hasStoredInitialMiniViewportRef = useRef(Boolean(readStoredViewport("trends-mini", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("trends", areaId, "initial")
  );
  const [miniFitRequestKey, setMiniFitRequestKey] = useState(() =>
    hasStoredInitialMiniViewportRef.current ? "" : makeAutoFitRequestKey("trends-mini", areaId, "initial")
  );

  const loadBundle = useCallback(
    async (targetAreaId, electionId, signal) => {
      const cacheKey = `${targetAreaId}:${electionId}`;
      if (!bundleCacheRef.current.has(cacheKey)) {
        bundleCacheRef.current.set(
          cacheKey,
          Promise.all([
            requestJson(buildAreaDataUrl("results", targetAreaId, electionId, apiBase), signal, apiBase),
            requestJson(buildAreaDataUrl("stats", targetAreaId, electionId, apiBase), signal, apiBase),
            requestJson(buildAreaDataUrl("districts/geojson", targetAreaId, electionId, apiBase), signal, apiBase),
          ]).then(([results, stats, districtsGeoJson]) => ({
            results: Array.isArray(results) ? results : [],
            stats: Array.isArray(stats) ? stats : [],
            districtsGeoJson:
              districtsGeoJson && districtsGeoJson.type === "FeatureCollection"
                ? districtsGeoJson
                : { type: "FeatureCollection", features: [] },
          }))
        );
      }
      return bundleCacheRef.current.get(cacheKey);
    },
    [apiBase]
  );

  useEffect(() => {
    if (lastAreaIdRef.current !== areaId) {
      bundleCacheRef.current = new Map();
      lastAreaIdRef.current = areaId;
      hasRestoredSelectionRef.current = false;
      setSelectorBundles({});
      setResolvedRun(null);
      setTooltip(null);
      setHoveredPrecinct(null);
      setSelectedPrecinctIds(new Set());
      clearStoredViewport("trends", areaId);
      clearStoredViewport("trends-mini", areaId);
      setFitRequestKey(makeAutoFitRequestKey("trends", areaId, "area"));
      setMiniFitRequestKey(makeAutoFitRequestKey("trends-mini", areaId, "area"));
    }
  }, [areaId]);

  const resetMapsToArea = useCallback(() => {
    setSelectedPrecinctIds(new Set());
    setHoveredPrecinct(null);
    setTooltip(null);
    clearStoredViewport("trends", areaId);
    clearStoredViewport("trends-mini", areaId);
    setFitRequestKey(makeAutoFitRequestKey("trends", areaId, "reset"));
    setMiniFitRequestKey(makeAutoFitRequestKey("trends-mini", areaId, "reset"));
  }, [areaId]);

  useEffect(() => {
    if (!areaId) return;
    let cancelled = false;
    const ac = new AbortController();
    setIsLoadingElections(true);
    setElectionsError("");

    (async () => {
      try {
        const payload = await requestJson(buildAreaElectionsUrl(areaId, apiBase), ac.signal, apiBase);
        if (cancelled) return;
        const matrix = buildTrendsElectionMatrix(sortElectionsNewestFirst(Array.isArray(payload) ? payload : []));
        const storedSelection = readElectionSelectionContext(areaId);
        const shouldRestoreSelection = !hasRestoredSelectionRef.current;
        if (shouldRestoreSelection) {
          hasRestoredSelectionRef.current = true;
          setPendingEntityValues(normalizeStoredObject(storedSelection.trendsPendingEntityValues));
          const storedUiState = normalizeStoredObject(storedSelection.trendsUiState);
          if (storedUiState.viewMode === "graph" || storedUiState.viewMode === "list") {
            setViewMode(storedUiState.viewMode);
          }
          if (TRENDS_SORT_OPTIONS.some((option) => option.key === storedUiState.sortKey)) {
            setSortKey(storedUiState.sortKey);
          }
          const allowedStoredStateFilters = new Set(
            getStateFilterOptions(2).concat(getStateFilterOptions(3)).map((option) => option.key)
          );
          if (allowedStoredStateFilters.has(storedUiState.stateFilter)) {
            setStateFilter(storedUiState.stateFilter);
          }
          if (typeof storedUiState.showBaselineArea === "boolean") {
            setShowBaselineArea(storedUiState.showBaselineArea);
          }
        }
        setElectionsMatrix(matrix);
        setDraftQuery((current) => {
          const nextDefault = buildDefaultDraftQuery(matrix);
          if (shouldRestoreSelection) {
            return restoreTrendsDraftQuery(matrix, storedSelection.trendsDraftQuery);
          }
          if (!current || !getDefaultTrendsFamily(matrix)) return nextDefault;
          const nextFamily =
            current.family && Array.isArray(matrix[current.family]) && matrix[current.family].length > 0
              ? current.family
              : nextDefault.family;
          const allowedYears = new Set((matrix[nextFamily] || []).map((item) => item.year));
          const nextSelectedYears = (Array.isArray(current.selectedYears) ? current.selectedYears : [])
            .map((year) => Number(year))
            .filter((year) => allowedYears.has(year));
          return {
            ...current,
            family: nextFamily,
            selectedYears: nextSelectedYears.length >= 2 ? nextSelectedYears : nextDefault.selectedYears,
          };
        });
      } catch (error) {
        if (cancelled || error.name === "AbortError") return;
        setElectionsMatrix(createEmptyTrendsElectionMatrix());
        setElectionsError(formatErrorMessage(error, "Nepodařilo se načíst dostupné volby."));
      } finally {
        if (!cancelled) setIsLoadingElections(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [areaId, apiBase]);

  useEffect(() => {
    if (!areaId || !getDefaultTrendsFamily(electionsMatrix)) return;
    const applied = buildAppliedQueryFromDraft(draftQuery, electionsMatrix);
    const electionIds = applied.years.map((entry) => String(entry.election.id || "")).filter(Boolean);
    writeElectionSelectionContext(areaId, {
      trendsDraftQuery: draftQuery,
      trendsElectionIds: electionIds,
      trendsPendingEntityValues: pendingEntityValues,
      trendsUiState: {
        viewMode,
        sortKey,
        stateFilter,
        showBaselineArea,
      },
      ...(electionIds.length > 0 ? { lastElectionId: electionIds[electionIds.length - 1] } : null),
    });
  }, [areaId, draftQuery, electionsMatrix, pendingEntityValues, showBaselineArea, sortKey, stateFilter, viewMode]);

  const familyYears = useMemo(
    () => (Array.isArray(electionsMatrix && electionsMatrix[draftQuery.family]) ? electionsMatrix[draftQuery.family] : []),
    [draftQuery.family, electionsMatrix]
  );

  useEffect(() => {
    if (!areaId || familyYears.length === 0) {
      setSelectorBundles({});
      return;
    }
    const activeElectionIds = familyYears
      .filter((item) => (draftQuery.selectedYears || []).includes(item.year))
      .map((item) => item.election.id);
    if (draftQuery.analysisMode !== PARTY_ANALYSIS_MODE || activeElectionIds.length === 0) {
      setSelectorBundles({});
      setSelectorError("");
      setIsLoadingSelectors(false);
      return;
    }

    let cancelled = false;
    const ac = new AbortController();
    setIsLoadingSelectors(true);
    setSelectorError("");

    Promise.all(activeElectionIds.map((electionId) => loadBundle(areaId, electionId, ac.signal)))
      .then((bundles) => {
        if (cancelled) return;
        const next = {};
        activeElectionIds.forEach((electionId, index) => {
          next[electionId] = bundles[index];
        });
        setSelectorBundles(next);
      })
      .catch((error) => {
        if (cancelled || error.name === "AbortError") return;
        setSelectorError(formatErrorMessage(error, "Nepodařilo se načíst entity pro vybrané roky."));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSelectors(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [areaId, draftQuery.analysisMode, draftQuery.selectedYears, familyYears, loadBundle]);

  const applyRun = useCallback(() => {
    const query = buildAppliedQueryFromDraft(draftQuery, electionsMatrix);
    if (query.years.length < 2 || query.years.length > 3) return;
    setIsRunning(true);
    setRunError("");
    let cancelled = false;
    const ac = new AbortController();

    Promise.all(query.years.map((entry) => loadBundle(areaId, entry.election.id, ac.signal)))
      .then((bundles) => {
        if (cancelled) return;
        const bundlesByElectionId = {};
        query.years.forEach((entry, index) => {
          bundlesByElectionId[entry.election.id] = bundles[index];
        });
        startTransition(() => {
          setResolvedRun({
            appliedQuery: query,
            bundlesByElectionId,
          });
        });
      })
      .catch((error) => {
        if (cancelled || error.name === "AbortError") return;
        setRunError(formatErrorMessage(error, "Nepodařilo se načíst data pro Trendy."));
      })
      .finally(() => {
        if (!cancelled) setIsRunning(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [areaId, draftQuery, electionsMatrix, loadBundle]);

  const runAnalysis = useMemo(() => {
    if (!resolvedRun) return null;
    const baseAnalysis = buildTrendRows(resolvedRun.appliedQuery, resolvedRun.bundlesByElectionId);
    const areaBaselinePoints = buildAreaBaselinePoints(resolvedRun.appliedQuery, resolvedRun.bundlesByElectionId);
    return {
      ...baseAnalysis,
      areaBaselinePoints,
      rows: baseAnalysis.rows.map((row) => ({
        ...row,
        areaBaselinePoints,
      })),
    };
  }, [resolvedRun]);

  const filteredRows = useMemo(() => {
    if (!runAnalysis || !resolvedRun) return [];
    return buildLowerSectionRows(runAnalysis.rows, {
      sortKey,
      stateFilter,
      limitKey: "all",
      selectedYearsCount: resolvedRun.appliedQuery.years.length,
    });
  }, [resolvedRun, runAnalysis, sortKey, stateFilter]);

  const deferredRows = useDeferredValue(filteredRows);

  const rowByPrecinct = useMemo(() => {
    const map = new Map();
    (runAnalysis && Array.isArray(runAnalysis.rows) ? runAnalysis.rows : []).forEach((row) => {
      map.set(row.precinctId, row);
    });
    return map;
  }, [runAnalysis]);

  const newestBundle = useMemo(() => {
    if (!resolvedRun) return null;
    const newest = resolvedRun.appliedQuery.years[resolvedRun.appliedQuery.years.length - 1];
    return resolvedRun.bundlesByElectionId[newest.election.id] || null;
  }, [resolvedRun]);

  const legendItems = useMemo(() => {
    if (!resolvedRun) return [];
    if (resolvedRun.appliedQuery.years.length === 2) {
      return [
        { kind: "positive", label: "Růst" },
        { kind: "neutral", label: "Beze změny" },
        { kind: "negative", label: "Pokles" },
        { kind: "no_data", label: "N/A / mismatch" },
      ];
    }
    return [
      { kind: "up_up", label: "(růst, růst)" },
      { kind: "down_down", label: "(pokles, pokles)" },
      { kind: "down_up", label: "(pokles, růst)" },
      { kind: "up_down", label: "(růst, pokles)" },
      { kind: "flat_transition", label: "Obsahuje 0" },
      { kind: "no_data", label: "N/A / mismatch" },
    ];
  }, [resolvedRun]);

  const toggleSelectedPrecinct = useCallback((precinctId) => {
    const nextPrecinctId = String(precinctId || "").trim();
    if (!nextPrecinctId) return;
    setSelectedPrecinctIds((current) => {
      const next = new Set(current);
      if (next.has(nextPrecinctId)) next.delete(nextPrecinctId);
      else next.add(nextPrecinctId);
      return next;
    });
  }, []);

  const styleFeature = useCallback(
    (feature) => {
      const precinctId = String(feature && feature.properties ? feature.properties.okrsek || "" : "").trim();
      const row = rowByPrecinct.get(precinctId);
      const stateMeta = getTrendStateMeta(row);
      const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === precinctId;
      const isSelected = selectedPrecinctIds.has(precinctId);
      return getElectionMapFeatureStyle({
        fillColor: stateMeta.fill,
        noDataFillColor: "#e5e7eb",
        isHovered,
        isSelected,
        clearOnFocus: true,
      });
    },
    [hoveredPrecinct, rowByPrecinct, selectedPrecinctIds]
  );

  const positionTooltipFromEvent = useCallback((precinctId, event) => {
    const clientX = event && event.originalEvent ? event.originalEvent.clientX : 0;
    const clientY = event && event.originalEvent ? event.originalEvent.clientY : 0;
    setTooltip({
      precinctId,
      x: Math.max(18, Math.min(clientX + 18, window.innerWidth - 340)),
      y: Math.max(18, Math.min(clientY + 18, window.innerHeight - 220)),
    });
  }, []);

  const moveTooltipFromEvent = useCallback((precinctId, event) => {
    const clientX = event && event.originalEvent ? event.originalEvent.clientX : 0;
    const clientY = event && event.originalEvent ? event.originalEvent.clientY : 0;
    setTooltip((current) =>
      current && current.precinctId === precinctId
        ? {
            ...current,
            x: Math.max(18, Math.min(clientX + 18, window.innerWidth - 340)),
            y: Math.max(18, Math.min(clientY + 18, window.innerHeight - 220)),
          }
        : current
    );
  }, []);

  const onEachFeature = useCallback(
    (feature, layer) => {
      const precinctId = String(feature && feature.properties ? feature.properties.okrsek || "" : "").trim();
      if (!precinctId) return;
      layer.on({
        mouseover: (event) => {
          const row = rowByPrecinct.get(precinctId);
          if (!row || !resolvedRun) return;
          setHoveredPrecinct(precinctId);
          positionTooltipFromEvent(precinctId, event);
        },
        mousemove: (event) => {
          moveTooltipFromEvent(precinctId, event);
        },
        click: () => {
          toggleSelectedPrecinct(precinctId);
        },
        mouseout: () => {
          setHoveredPrecinct((current) => (current === precinctId ? null : current));
          setTooltip((current) => (current && current.precinctId === precinctId ? null : current));
        },
      });
    },
    [moveTooltipFromEvent, positionTooltipFromEvent, resolvedRun, rowByPrecinct, toggleSelectedPrecinct]
  );

  const onEachMiniMapFeature = useCallback(
    (feature, layer) => {
      const precinctId = String(feature && feature.properties ? feature.properties.okrsek || "" : "").trim();
      if (!precinctId) return;
      layer.on({
        mouseover: (event) => {
          const row = rowByPrecinct.get(precinctId);
          if (!row || !resolvedRun) return;
          setHoveredPrecinct(precinctId);
          positionTooltipFromEvent(precinctId, event);
        },
        mousemove: (event) => {
          moveTooltipFromEvent(precinctId, event);
        },
        mouseout: () => {
          setHoveredPrecinct((current) => (current === precinctId ? null : current));
          setTooltip((current) => (current && current.precinctId === precinctId ? null : current));
        },
        click: () => {
          toggleSelectedPrecinct(precinctId);
        },
      });
    },
    [moveTooltipFromEvent, positionTooltipFromEvent, resolvedRun, rowByPrecinct, toggleSelectedPrecinct]
  );

  const tooltipRow = tooltip ? rowByPrecinct.get(tooltip.precinctId) || null : null;
  const appliedConfig = useMemo(
    () =>
      resolvedRun
        ? {
            analysisMode: resolvedRun.appliedQuery.analysisMode,
            valueMode: resolvedRun.appliedQuery.valueMode,
            metricKey: resolvedRun.appliedQuery.metricKey,
          }
        : {
            analysisMode: PARTY_ANALYSIS_MODE,
            valueMode: "percentage",
            metricKey: TRENDS_METRICS[0].key,
          },
    [resolvedRun]
  );
  const tooltipFields =
    tooltipRow && resolvedRun ? buildTooltipFields(tooltipRow, appliedConfig, resolvedRun.appliedQuery) : null;

  const isDraftDirty = useMemo(() => {
    if (!resolvedRun) return false;
    const draftSnapshot = normalizeTrendsQuerySnapshot(buildAppliedQueryFromDraft(draftQuery, electionsMatrix));
    const appliedSnapshot = normalizeTrendsQuerySnapshot(resolvedRun.appliedQuery);
    return JSON.stringify(draftSnapshot) !== JSON.stringify(appliedSnapshot);
  }, [draftQuery, electionsMatrix, resolvedRun]);

  const hasVisibleAppliedRun = Boolean(resolvedRun && !isDraftDirty);

  const baselinePreview = useMemo(() => {
    if (!showBaselineArea || !runAnalysis) {
      return { values: [] };
    }
    return { values: buildBaselinePreview(runAnalysis.areaBaselinePoints, appliedConfig) };
  }, [appliedConfig, runAnalysis, showBaselineArea]);

  const familyButtons = useMemo(
    () =>
      TRENDS_FAMILY_ORDER.map((key) => ({
        key,
        label: TRENDS_FAMILY_CONFIG[key].label,
        disabled: !(Array.isArray(electionsMatrix && electionsMatrix[key]) && electionsMatrix[key].length > 0),
      })),
    [electionsMatrix]
  );

  const summaryLine = useMemo(() => {
    if (!runAnalysis) return "";
    const mismatchCount = runAnalysis.mismatchSummary.newestMissingHistory;
    const olderOnlyCount = runAnalysis.mismatchSummary.olderOnlyDistricts.length;
    return `${runAnalysis.rows.length} okrsků v nejnovější geometrii · ${mismatchCount} N/A / mismatch · ${olderOnlyCount} starších-only okrsků mimo mapu`;
  }, [runAnalysis]);

  useEffect(() => {
    setSelectedPrecinctIds((current) => {
      const next = new Set();
      current.forEach((precinctId) => {
        if (rowByPrecinct.has(String(precinctId))) next.add(precinctId);
      });
      return next.size === current.size ? current : next;
    });
  }, [rowByPrecinct]);

  return (
    <>
      <style>{`
        .trends-page {
          --trends-active-blue: ${BRAND_COLORS.primaryBrightBlue};
          --trends-cta-red: ${BRAND_COLORS.accentRed};
          width: 100%;
          padding: 12px 0 44px;
          display: grid;
          gap: 12px;
          color: #0f172a;
          background: ${APP_PAGE_BACKGROUND};
        }
        .trends-header {
          display: grid;
          gap: 4px;
          padding: 0;
          max-width: 100%;
        }
        .trends-header h1 {
          margin: 0;
          font-size: clamp(2rem, 3vw, 2.8rem);
          line-height: 1.02;
          letter-spacing: -0.04em;
          font-weight: 900;
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .trends-header p {
          margin: 0;
          color: #425b76;
          font-size: 1rem;
          line-height: 1.55;
          max-width: 110ch;
        }
        .trends-card {
          border: 1px solid #dbe5f1;
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 22px 48px rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(10px);
        }
        .trends-control-panel {
          padding: 16px;
          display: grid;
          gap: 10px;
        }
        .trends-panel-title {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .trends-panel-title h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 900;
          letter-spacing: -0.03em;
        }
        .trends-panel-title span {
          color: #64748b;
          font-size: 0.9rem;
        }
        .trends-grid {
          display: grid;
          gap: var(--app-shell-major-gap, 12px);
        }
        .trends-grid-columns {
          display: grid;
          gap: 12px;
          grid-template-columns: minmax(0, 1.18fr) minmax(0, 1fr);
          align-items: start;
        }
        .trends-query-block {
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          padding: 12px;
          display: grid;
          gap: 8px;
          align-content: start;
        }
        .trends-query-controls {
          gap: 9px;
        }
        .trends-query-selection {
          gap: 9px;
        }
        .trends-block-label {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
        }
        .trends-segment-row,
        .trends-year-row,
        .trends-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .trends-segment,
        .trends-year-chip,
        .trends-run-button,
        .trends-selection-chip {
          border: 1px solid #cbd5e1;
          border-radius: 999px;
          background: #ffffff;
          color: #0f172a;
          cursor: pointer;
          transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
        }
        .trends-segment,
        .trends-year-chip,
        .trends-run-button {
          padding: 9px 14px;
          font-size: 0.88rem;
          font-weight: 700;
        }
        .trends-segment:hover,
        .trends-year-chip:hover,
        .trends-run-button:hover,
        .trends-selection-chip:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }
        .trends-segment.is-active,
        .trends-year-chip.is-selected {
          border-color: var(--trends-active-blue);
          background: var(--trends-active-blue);
          color: #ffffff;
        }
        .trends-year-chip:disabled,
        .trends-run-button:disabled {
          cursor: not-allowed;
          opacity: 0.48;
          transform: none;
          box-shadow: none;
        }
        .trends-select {
          width: 100%;
          min-width: 0;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          min-height: 34px;
          padding: 7px 10px;
          background: #ffffff;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 600;
          box-sizing: border-box;
        }
        .trends-field {
          display: grid;
          gap: 5px;
        }
        .trends-field span {
          font-size: 0.76rem;
          color: #475569;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .trends-entity-stack {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 8px;
          align-items: start;
        }
        .trends-entity-panel {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #ffffff;
          padding: 8px;
          display: grid;
          gap: 5px;
          min-height: 98px;
          align-content: start;
        }
        .trends-entity-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
        }
        .trends-entity-header strong {
          font-size: 0.9rem;
        }
        .trends-entity-header span {
          color: #64748b;
          font-size: 0.78rem;
        }
        .trends-entity-controls {
          display: block;
        }
        .trends-analysis-row {
          display: grid;
          grid-template-columns: minmax(0, auto) minmax(210px, 0.72fr);
          gap: 8px;
          align-items: end;
        }
        .trends-analysis-row .trends-segment-row {
          align-items: center;
        }
        .trends-metric-inline {
          min-width: min(260px, 100%);
        }
        .trends-selection-chip {
          padding: 4px 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.78rem;
          font-weight: 700;
        }
        .trends-entity-guidance {
          min-height: 14px;
          font-size: 0.76rem;
          color: #5b7088;
          line-height: 1.35;
        }
        .trends-chip-row {
          align-content: start;
          min-height: 26px;
        }
        .trends-empty-hint,
        .trends-help-text {
          font-size: 0.8rem;
          color: #64748b;
          line-height: 1.35;
        }
        .trends-metric-note {
          border: 1px dashed #cbd5e1;
          border-radius: 12px;
          background: #f8fafc;
          color: #64748b;
          padding: 10px 12px;
          display: grid;
          gap: 4px;
        }
        .trends-metric-note-title {
          color: #0f172a;
          font-size: 0.9rem;
          font-weight: 800;
        }
        .trends-metric-note-text {
          font-size: 0.82rem;
          line-height: 1.35;
        }
        .trends-run-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
          justify-content: flex-end;
        }
        .trends-run-row .trends-help-text {
          flex: 1 1 320px;
        }
        .trends-run-row .trends-status {
          flex: 0 1 auto;
        }
        .trends-run-button {
          border-color: var(--trends-cta-red);
          background: var(--trends-cta-red);
          color: #ffffff;
          padding-inline: 18px;
          box-shadow: 0 12px 24px rgba(226, 16, 57, 0.22);
          flex: 0 0 auto;
        }
        .trends-status,
        .trends-error {
          border-radius: 16px;
          padding: 12px 14px;
          font-size: 0.9rem;
        }
        .trends-status {
          background: #eff6ff;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        }
        .trends-status.is-dirty {
          background: #fffbea;
          color: #92400e;
          border-color: #fcd34d;
        }
        .trends-error {
          background: #fff1f2;
          color: #9f1239;
          border: 1px solid #fecdd3;
        }
        .trends-map-card,
        .trends-lower-card {
          padding: 18px;
          display: grid;
          gap: var(--app-shell-major-gap, 12px);
        }
        .trends-map-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .trends-map-head h2,
        .trends-lower-head h2 {
          margin: 0;
          font-size: 1.16rem;
          font-weight: 900;
          letter-spacing: -0.03em;
        }
        .trends-map-summary {
          color: #64748b;
          font-size: 0.88rem;
        }
        .trends-map-shell {
          position: relative;
          border: 1px solid #d9e4ef;
          border-radius: 24px;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          min-height: 430px;
        }
        .trends-map-surface {
          padding: 12px;
        }
        .trends-map-overlay {
          position: absolute;
          inset: 0;
          display: grid;
          place-items: center;
          background: rgba(248, 250, 252, 0.62);
          backdrop-filter: blur(3px);
          font-weight: 800;
          color: #0f172a;
          pointer-events: none;
        }
        .trends-empty-surface {
          min-height: 430px;
          display: grid;
          place-items: center;
          gap: 6px;
          text-align: center;
          padding: 24px;
          color: #475569;
          background:
            linear-gradient(135deg, rgba(29, 78, 216, 0.06), transparent 48%),
            linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
        }
        .trends-empty-title {
          font-size: 1.05rem;
          font-weight: 800;
          color: #0f172a;
        }
        .trends-empty-text {
          max-width: 48ch;
          line-height: 1.55;
        }
        .trends-legend-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 14px;
        }
        .trends-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #334155;
          font-size: 0.84rem;
          font-weight: 700;
        }
        .trends-legend-swatch {
          width: 24px;
          height: 16px;
          border-radius: 7px;
          border: 1px solid #334155;
          background-position: center;
          background-size: cover;
          flex: 0 0 auto;
        }
        .trends-hover-tooltip {
          position: fixed;
          z-index: 1300;
          pointer-events: none;
          max-width: 320px;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.16);
          padding: 12px 14px;
          font-size: 12px;
          color: #111827;
        }
        .trends-hover-tooltip .tt-title {
          font-weight: 900;
          margin-bottom: 6px;
        }
        .trends-hover-tooltip .tt-support,
        .trends-hover-tooltip .tt-row,
        .trends-hover-tooltip .tt-delta {
          line-height: 1.45;
          color: #475569;
        }
        .trends-hover-tooltip .tt-state {
          margin-top: 6px;
          font-weight: 800;
          color: #0f172a;
        }
        .trends-mismatch-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .trends-mismatch-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #cbd5e1;
          border-radius: 999px;
          padding: 6px 10px;
          background: #ffffff;
          color: #334155;
          font-size: 0.82rem;
          font-weight: 700;
        }
        .trends-lower-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .trends-lower-layout {
          display: grid;
          grid-template-columns: minmax(570px, 0.86fr) minmax(460px, 0.72fr);
          gap: var(--app-shell-major-column-gap, 18px);
          align-items: start;
        }
        .trends-lower-main {
          display: grid;
          gap: 14px;
          min-width: 0;
        }
        .trends-lower-rail {
          min-height: 100%;
          border-left: 1px solid #e2e8f0;
          padding-left: 20px;
          padding-top: 32px;
        }
        .trends-lower-rail-placeholder {
          position: sticky;
          top: calc(var(--app-shell-topbar-offset, 0px) + 14px);
          min-height: 260px;
          border: 1px dashed #cbd5e1;
          border-radius: 20px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          color: #64748b;
          display: grid;
          place-items: center;
          text-align: center;
          padding: 20px;
          line-height: 1.45;
        }
        .trends-sticky-mini-map {
          position: sticky;
          top: calc(var(--app-shell-topbar-offset, 0px) + 14px);
          border: 1px solid #dbe4ef;
          border-radius: 20px;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08);
        }
        .trends-sticky-mini-map .leaflet-container {
          min-height: 560px;
        }
        .trends-lower-toolbar {
          display: grid;
          grid-template-columns: minmax(0, auto) minmax(220px, 1fr);
          gap: 8px;
          align-items: end;
        }
        .trends-toolbar-subrow {
          display: grid;
          grid-template-columns: minmax(160px, 1fr) minmax(160px, 1fr);
          gap: 8px;
          align-items: end;
        }
        .trends-toolbar-field {
          display: grid;
          gap: 6px;
          min-width: 0;
        }
        .trends-toolbar-field > span {
          font-size: 0.78rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #64748b;
        }
        .trends-empty-panel {
          border: 1px dashed #cbd5e1;
          border-radius: 18px;
          background: #f8fafc;
          color: #64748b;
          padding: 18px;
        }
        .trends-graph-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .trends-graph-card {
          border: 1px solid #dbe4ef;
          border-radius: 12px;
          background: #ffffff;
          padding: 7px;
          display: grid;
          gap: 4px;
          cursor: pointer;
          transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
        }
        .trends-graph-card.is-hovered {
          border-color: #93c5fd;
          box-shadow: 0 14px 32px rgba(59, 130, 246, 0.14);
        }
        .trends-graph-card.is-selected {
          border-color: #1d4ed8;
          box-shadow: 0 18px 38px rgba(37, 99, 235, 0.18);
        }
        .trends-graph-header,
        .trends-graph-footer {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
        }
        .trends-graph-title {
          font-size: 0.78rem;
          font-weight: 800;
        }
        .trends-graph-subtitle,
        .trends-graph-footer span {
          color: #64748b;
          font-size: 0.64rem;
        }
        .trends-state-pill {
          display: inline-flex;
          align-items: center;
          padding: 2px 6px;
          border: 1px solid #cbd5e1;
          border-radius: 999px;
          font-size: 0.64rem;
          font-weight: 800;
          background: #ffffff;
          white-space: nowrap;
        }
        .trends-sparkline {
          display: block;
          width: 100%;
          height: auto;
        }
        .trends-sparkline-point-label {
          font-size: 4.6px;
          font-weight: 800;
          fill: #334155;
        }
        .trends-baseline-toggle {
          justify-content: flex-start;
          min-width: 0;
          width: 100%;
          text-align: left;
          white-space: normal;
          overflow: visible;
          text-overflow: clip;
        }
        .trends-baseline-toggle-values {
          color: inherit;
          font-weight: 900;
        }
        .trends-sparkline-empty {
          min-height: 44px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          background: #f8fafc;
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 700;
        }
        .trends-table-wrap {
          overflow: auto;
          border: 1px solid #dbe4ef;
          border-radius: 18px;
        }
        .trends-table {
          width: 100%;
          border-collapse: collapse;
          background: #ffffff;
          font-size: 0.9rem;
        }
        .trends-table th,
        .trends-table td {
          padding: 12px 13px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
          white-space: nowrap;
        }
        .trends-table-group-end {
          border-right: 16px solid #ffffff;
        }
        .trends-table th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f8fafc;
          color: #475569;
          font-size: 0.76rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .trends-table tbody tr:hover {
          background: #f8fbff;
        }
        .trends-table tbody tr.is-hovered {
          background: #eff6ff;
        }
        .trends-table tbody tr.is-selected {
          background: #dbeafe;
        }
        .trends-table-secondary {
          color: #64748b;
          font-size: 0.76rem;
          line-height: 1.35;
          margin-top: 2px;
        }
        .trends-table-delta {
          font-weight: 800;
        }
        .trends-table-delta.is-positive {
          color: #15803d;
        }
        .trends-table-delta.is-negative {
          color: #dc2626;
        }
        .trends-table-delta.is-neutral {
          color: #475569;
        }
        @media (max-width: 1220px) {
          .trends-grid-columns,
          .trends-lower-layout,
          .trends-lower-toolbar,
          .trends-toolbar-subrow {
            grid-template-columns: 1fr;
          }
          .trends-header p {
            max-width: 82ch;
          }
          .trends-lower-rail {
            border-left: 0;
            padding-left: 0;
            padding-top: 0;
          }
        }
        @media (max-width: 860px) {
          .trends-graph-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 760px) {
          .trends-analysis-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="trends-page">
        <section className="trends-card trends-control-panel">
          {electionsError ? <div className="trends-error">{electionsError}</div> : null}
          {selectorError ? <div className="trends-error">{selectorError}</div> : null}

          <div className="trends-grid trends-grid-columns">
            <div className="trends-query-block trends-query-controls">
              <div className="trends-field">
                <span className="trends-block-label">Skupina voleb</span>
                <div className="trends-segment-row">
                  {familyButtons.map((button) => (
                    <button
                      key={button.key}
                      type="button"
                      className={`trends-segment ${draftQuery.family === button.key ? "is-active" : ""}`}
                      disabled={button.disabled}
                      onClick={() => {
                        const family = button.key;
                        const selectedYears = (electionsMatrix[family] || []).map((item) => item.year);
                        setDraftQuery((current) => ({
                          ...current,
                          family,
                          selectedYears,
                          entitySelections: {},
                        }));
                        setPendingEntityValues({});
                      }}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="trends-field">
                <span className="trends-block-label">Roky</span>
                <div className="trends-year-row">
                  {familyYears.map((item) => {
                    const selected = (draftQuery.selectedYears || []).includes(item.year);
                    const selectedCount = (draftQuery.selectedYears || []).length;
                    const disabled = selected ? selectedCount <= 2 : selectedCount >= 3;
                    return (
                      <QueryYearButton
                        key={item.year}
                        label={String(item.year)}
                        selected={selected}
                        disabled={disabled}
                        onClick={() => {
                          setDraftQuery((current) => {
                            const currentYears = Array.isArray(current.selectedYears) ? current.selectedYears : [];
                            const hasYear = currentYears.includes(item.year);
                            return {
                              ...current,
                              selectedYears: hasYear
                                ? currentYears.filter((year) => year !== item.year)
                                : currentYears.concat(item.year).sort((a, b) => a - b),
                            };
                          });
                        }}
                      />
                    );
                  })}
                </div>
                <div className="trends-help-text">Vyber vždy 2 nebo 3 roky v rámci jedné volební rodiny.</div>
              </div>

              <div className="trends-field">
                <span className="trends-block-label">Zobrazit</span>
                <div className="trends-analysis-row">
                  <div className="trends-segment-row">
                    <button
                      type="button"
                      className={`trends-segment ${draftQuery.analysisMode === PARTY_ANALYSIS_MODE ? "is-active" : ""}`}
                      onClick={() =>
                        setDraftQuery((current) => ({
                          ...current,
                          analysisMode: PARTY_ANALYSIS_MODE,
                        }))
                      }
                    >
                      Strany
                    </button>
                    <button
                      type="button"
                      className={`trends-segment ${draftQuery.analysisMode === METRIC_ANALYSIS_MODE ? "is-active" : ""}`}
                      onClick={() =>
                        setDraftQuery((current) => ({
                          ...current,
                          analysisMode: METRIC_ANALYSIS_MODE,
                        }))
                      }
                    >
                      Voliči, hlasující, účast
                    </button>
                  </div>
                  {draftQuery.analysisMode === METRIC_ANALYSIS_MODE ? (
                    <select
                      className="trends-select trends-metric-inline"
                      aria-label="Voliči, hlasující, účast"
                      value={draftQuery.metricKey}
                      onChange={(event) =>
                        setDraftQuery((current) => ({
                          ...current,
                          metricKey: event.target.value,
                        }))
                      }
                    >
                      {TRENDS_METRICS.map((metric) => (
                        <option key={metric.key} value={metric.key}>
                          {metric.label}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
              </div>

              {draftQuery.analysisMode === PARTY_ANALYSIS_MODE ? (
                <div className="trends-field">
                  <span className="trends-block-label">Porovnání</span>
                  <div className="trends-segment-row">
                    <button
                      type="button"
                      className={`trends-segment ${draftQuery.valueMode === "percentage" ? "is-active" : ""}`}
                      onClick={() =>
                        setDraftQuery((current) => ({
                          ...current,
                          valueMode: "percentage",
                        }))
                      }
                    >
                      Procenta
                    </button>
                    <button
                      type="button"
                      className={`trends-segment ${draftQuery.valueMode === "absolute" ? "is-active" : ""}`}
                      onClick={() =>
                        setDraftQuery((current) => ({
                          ...current,
                          valueMode: "absolute",
                        }))
                      }
                    >
                      Absolutní hlasy
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="trends-query-block trends-query-selection">
              <div className="trends-panel-title">
                <h2>Výběr stran</h2>
                <span>{draftQuery.analysisMode === PARTY_ANALYSIS_MODE ? "Výběr je oddělený po jednotlivých letech." : "Tento režim nevyžaduje výběr stran."}</span>
              </div>

              {draftQuery.analysisMode === PARTY_ANALYSIS_MODE ? (
                <div className="trends-entity-stack">
                  {familyYears
                    .filter((item) => (draftQuery.selectedYears || []).includes(item.year))
                    .map((item) => (
                      <EntitySelector
                        key={item.election.id}
                        yearEntry={item}
                        bundle={selectorBundles[item.election.id]}
                        selectedEntities={(draftQuery.entitySelections && draftQuery.entitySelections[item.election.id]) || []}
                        pendingValues={pendingEntityValues}
                        setPendingValues={setPendingEntityValues}
                        isLoading={isLoadingSelectors || !selectorBundles[item.election.id]}
                        onAddEntity={(electionId, entity) =>
                          setDraftQuery((current) => ({
                            ...current,
                            entitySelections: {
                              ...(current.entitySelections || {}),
                              [electionId]: sortEntitySelections(
                                ((current.entitySelections && current.entitySelections[electionId]) || []).concat(entity)
                              ),
                            },
                          }))
                        }
                        onRemoveEntity={(electionId, entityKey) =>
                          setDraftQuery((current) => ({
                            ...current,
                            entitySelections: {
                              ...(current.entitySelections || {}),
                              [electionId]: ((current.entitySelections && current.entitySelections[electionId]) || []).filter(
                                (itemSelection) => itemSelection.key !== entityKey
                              ),
                            },
                          }))
                        }
                      />
                    ))}
                </div>
              ) : (
                <div className="trends-metric-note">
                  <div className="trends-metric-note-title">Voliči, hlasující, účast</div>
                  <div className="trends-metric-note-text">
                    Vyber metriku vlevo a spusť výpočet. Výběr stran se pro tento režim nepoužívá.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="trends-run-row">
            <div className="trends-help-text">
              {isLoadingElections
                ? "Načítám katalog voleb…"
                : 'Mapa i spodní analytika se aktualizují až po kliknutí na "Ukázat trendy".'}
            </div>
            {isDraftDirty ? (
              <div className="trends-status is-dirty">Dotaz se změnil. Klikněte na „Ukázat trendy“.</div>
            ) : null}
            <button type="button" className="trends-run-button" onClick={applyRun} disabled={!isDraftQueryValid(draftQuery, electionsMatrix) || isRunning}>
              {isRunning ? "Počítám trendy…" : "Ukázat trendy"}
            </button>
          </div>
        </section>

        {hasVisibleAppliedRun && runAnalysis?.boundaryComparisonBlocked ? (
          <p role="status" className="trends-status">{BOUNDARY_CHANGE_NOTICE}</p>
        ) : null}
        <section className="trends-card trends-map-card">
          <div className="trends-map-head">
            <h2>Map</h2>
            <div className="trends-map-summary">{hasVisibleAppliedRun ? summaryLine || "Trendy se zobrazí po spuštění dotazu." : "Trendy se zobrazí po spuštění dotazu."}</div>
          </div>

          <div className="trends-map-shell">
            {!hasVisibleAppliedRun || !newestBundle ? (
              <EmptySurface
                title={resolvedRun ? "Mapa čeká na nové spuštění" : "Mapa zatím čeká na první běh"}
                text={
                  resolvedRun
                    ? "Dotaz se změnil. Klikněte na „Ukázat trendy“."
                    : "Po nastavení voleb, let a entit klikni na „Ukázat trendy“. Výsledek se vždy kreslí podle geometrie nejnovějšího vybraného roku."
                }
              />
            ) : (
              <div className="trends-map-surface">
                <MapPane
                  mapKey={`trends-${areaId || "none"}`}
                  geoJsonKey={resolvedRun.appliedQuery.years.map((entry) => entry.election.id).join("-")}
                  districtsGeoJson={newestBundle.districtsGeoJson}
                  styleFeature={styleFeature}
                  onEachFeature={onEachFeature}
                  mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
                  height="clamp(420px, 46vw, 700px)"
                  initialView={initialViewport}
                  fitGeoJsonBounds
                  fitBoundsRequestKey={fitRequestKey}
                  fitBoundsPadding={[22, 22]}
                  fitBoundsMaxZoom={15}
                  scrollWheelZoom={false}
                  onMapReady={(map) => {
                    mapRef.current = map;
                  }}
                  onViewChange={(center, zoom) => {
                    writeStoredViewport("trends", areaId, { center, zoom });
                  }}
                  onMouseLeave={() => {
                    setHoveredPrecinct(null);
                    setTooltip(null);
                  }}
                  onInteractionStart={() => {
                    setHoveredPrecinct(null);
                    setTooltip(null);
                  }}
                  onResetView={resetMapsToArea}
                  resetControlDisabled={!hasVisibleAppliedRun}
                />
              </div>
            )}

            {isRunning && resolvedRun ? <div className="trends-map-overlay">Přepočítávám trendy…</div> : null}
          </div>

          <div className="trends-query-block">
            <div className="trends-block-label">Legenda</div>
            <div className="trends-legend-list">
              {legendItems.map((item) => (
                <div key={item.label} className="trends-legend-item">
                  <LegendSwatch kind={item.kind} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {runError ? <div className="trends-error">{runError}</div> : null}

          {hasVisibleAppliedRun && runAnalysis && runAnalysis.mismatchSummary.olderOnlyDistricts.length > 0 ? (
            <div className="trends-query-block">
              <div className="trends-block-label">Older-only mismatch summary</div>
              <div className="trends-help-text">
                Tyto okrsky existují pouze ve starších vybraných letech a nejsou v geometrii nejnovějšího roku, proto se v mapě nekreslí.
              </div>
              <div className="trends-mismatch-list">
                {runAnalysis.mismatchSummary.olderOnlyDistricts.slice(0, 24).map((item) => (
                  <span key={`${item.year}-${item.precinctId}`} className="trends-mismatch-chip">
                    <span>{item.precinctCode || getOkrsekTail(item.precinctId)}</span>
                    <span>{item.year}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="trends-card trends-lower-card">
          <div className="trends-lower-head">
            <h2>Grafy a tabulky</h2>
            <div className="trends-map-summary">
              {hasVisibleAppliedRun ? `${deferredRows.length} řádků podle aktuálního filtru` : "Spodní analytika se zobrazí po prvním běhu."}
            </div>
          </div>
          <div className="trends-lower-layout">
            <div className="trends-lower-main">
              <LowerToolbar
                viewMode={viewMode}
                setViewMode={setViewMode}
                showBaselineArea={showBaselineArea}
                setShowBaselineArea={setShowBaselineArea}
                baselinePreview={baselinePreview}
              />

              <div className="trends-toolbar-subrow" data-testid="trends-toolbar-subrow">
                <label className="trends-toolbar-field" data-testid="trends-toolbar-sort">
                  <span>Řazení</span>
                  <select className="trends-select" value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
                    {TRENDS_SORT_OPTIONS.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="trends-toolbar-field" data-testid="trends-toolbar-state-filter">
                  <span>Filtr stavu</span>
                  <select className="trends-select" value={stateFilter} onChange={(event) => setStateFilter(event.target.value)}>
                    {getStateFilterOptions(resolvedRun && resolvedRun.appliedQuery.years.length === 2 ? 2 : 3).map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {!hasVisibleAppliedRun ? (
                <EmptySurface
                  title={resolvedRun ? "Analytická část čeká na nové spuštění" : "Analytická část je připravená"}
                  text={
                    resolvedRun
                      ? "Dotaz se změnil. Klikněte na „Ukázat trendy“."
                      : "Po prvním běhu se tu objeví grafový i tabulkový pohled se seřazením podle změny mezi nejstarším a nejnovějším rokem."
                  }
                />
              ) : viewMode === "graph" ? (
                <TrendGraphView
                  rows={deferredRows}
                  config={appliedConfig}
                  onHoverRow={setHoveredPrecinct}
                  onLeaveRow={() => setHoveredPrecinct(null)}
                  onToggleSelectRow={toggleSelectedPrecinct}
                  showBaseline={showBaselineArea}
                  hoveredPrecinct={hoveredPrecinct}
                  selectedPrecinctIds={selectedPrecinctIds}
                />
              ) : (
                <TrendListView
                  rows={deferredRows}
                  config={appliedConfig}
                  appliedQuery={resolvedRun.appliedQuery}
                  onHoverRow={setHoveredPrecinct}
                  onLeaveRow={() => setHoveredPrecinct(null)}
                  onToggleSelectRow={toggleSelectedPrecinct}
                  showBaseline={showBaselineArea}
                  hoveredPrecinct={hoveredPrecinct}
                  selectedPrecinctIds={selectedPrecinctIds}
                />
              )}
            </div>

            <aside className="trends-lower-rail">
              <TrendsStickyMiniMap
                visible={hasVisibleAppliedRun && Boolean(newestBundle)}
                districtsGeoJson={newestBundle ? newestBundle.districtsGeoJson : null}
                mapKey={
                  resolvedRun
                    ? `trends-mini-${areaId || "none"}`
                    : "trends-mini-empty"
                }
                geoJsonKey={
                  resolvedRun
                    ? resolvedRun.appliedQuery.years.map((entry) => entry.election.id).join("-")
                    : "empty"
                }
                styleFeature={styleFeature}
                onEachFeature={onEachMiniMapFeature}
                onMapReady={(map) => {
                  miniMapRef.current = map;
                }}
                onViewChange={(center, zoom) => {
                  writeStoredViewport("trends-mini", areaId, { center, zoom });
                }}
                initialView={initialMiniViewport}
                fitBoundsRequestKey={miniFitRequestKey}
                onResetView={resetMapsToArea}
                onMouseLeave={() => {
                  setHoveredPrecinct(null);
                  setTooltip(null);
                }}
              />
            </aside>
          </div>
        </section>

        {tooltip && tooltipFields ? (
          <div className="trends-hover-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
            <div className="tt-title">{tooltipFields.title}</div>
            {tooltipFields.support ? <div className="tt-support">{tooltipFields.support}</div> : null}
            <div className="tt-row">{tooltipFields.yearsLine}</div>
            <div className="tt-row">{tooltipFields.valuesLine}</div>
            {tooltipFields.deltaLines.map((line) => (
              <div key={line.label} className="tt-delta">{`${line.label}: ${line.value}`}</div>
            ))}
            <div className="tt-state">{`${tooltipFields.finalState} · ${tooltipFields.finalLabel}`}</div>
          </div>
        ) : null}
      </div>
    </>
  );
}
